import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Shuffle, Loader2, Clock, Sparkles, CheckCircle2, Crown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

function timeLeft(endsAt) {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return "ending…";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return `${d}d ${h}h left`;
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m left`;
}

export default function RemixChallengeBanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/challenges/current").then((r) => setData(r.data)).catch(() => setData({ challenge: null }));
  useEffect(() => { load(); }, []);

  if (!data || !data.challenge) return null;
  const ch = data.challenge;
  const active = ch.status === "active";

  const enter = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/games/remix-character", { source_game_id: ch.source_game_id, asset_id: ch.source_asset_id });
      toast.success(`You're in the “${ch.title}” challenge! Build & publish to climb the leaderboard. 🏆`);
      navigate(`/app/build/${r.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't enter the challenge");
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-8 rounded-3xl overflow-hidden border border-amber-400/30 bg-gradient-to-br from-[#2a1e05] via-[#1a1330] to-[#0b0e16] relative" data-testid="remix-challenge-banner">
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_15%_20%,#f59e0b40,transparent_45%)]" />
      <div className="relative p-5 sm:p-6 flex flex-col lg:flex-row gap-6">
        {/* Featured character */}
        <div className="flex items-center gap-4 lg:w-[340px] shrink-0">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden border border-amber-400/40 bg-black/30 shrink-0">
            {ch.character_thumbnail ? <img src={img(ch.character_thumbnail)} alt={ch.character_name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-3xl">🎮</div>}
          </div>
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold text-amber-300 bg-amber-400/15 border border-amber-400/30 mb-1.5">
              <Trophy className="w-3.5 h-3.5" /> WEEKLY REMIX CHALLENGE
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-white leading-tight truncate">{ch.title}</h2>
            <p className="text-xs text-white/60 mt-1 flex items-center gap-3">
              {active ? <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {timeLeft(ch.ends_at)}</span> : <span className="text-amber-300 font-semibold flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> Ended</span>}
              <span className="flex items-center gap-1"><Shuffle className="w-3.5 h-3.5" /> {data.entries_count} entries</span>
              <button onClick={() => navigate("/app/challenges")} className="hover:text-amber-300 underline underline-offset-2" data-testid="banner-hall-of-fame">Hall of Fame</button>
            </p>
          </div>
        </div>

        {/* CTA + leaderboard */}
        <div className="flex-1 min-w-0">
          {active ? (
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              {data.entered ? (
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-400" data-testid="challenge-entered"><CheckCircle2 className="w-4 h-4" /> You're entered — publish your game to rank!</span>
              ) : (
                <Button onClick={enter} disabled={busy} data-testid="challenge-enter-btn" className="rounded-full gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold hover:opacity-90">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Remix & enter
                </Button>
              )}
              <span className="text-xs text-white/50">Remix <b className="text-white/80">{ch.character_name}</b> — most-played entry wins the spotlight 🏆</span>
            </div>
          ) : data.winner ? (
            <button onClick={() => navigate(`/app/arcade/${data.winner.id}`)} className="mb-3 flex items-center gap-3 text-left rounded-2xl bg-amber-400/10 border border-amber-400/30 px-4 py-2.5 hover:bg-amber-400/15 transition-colors" data-testid="challenge-winner">
              <span className="text-2xl">{data.winner.cover_emoji}</span>
              <div className="min-w-0">
                <div className="text-[11px] font-bold text-amber-300 flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> WINNER</div>
                <div className="text-sm font-bold text-white truncate">{data.winner.title} <span className="text-white/50 font-normal">by {data.winner.owner_name}</span></div>
              </div>
            </button>
          ) : (
            <p className="text-sm text-white/50 mb-3">This challenge has ended.</p>
          )}

          {data.leaderboard?.length > 0 && (
            <ol className="space-y-1.5" data-testid="challenge-leaderboard">
              {data.leaderboard.slice(0, 3).map((g, i) => (
                <li key={g.id}>
                  <button onClick={() => navigate(`/app/arcade/${g.id}`)} className="w-full flex items-center gap-3 rounded-xl px-3 py-2 bg-white/5 hover:bg-white/10 transition-colors text-left" data-testid={`challenge-lb-${i}`}>
                    <span className="w-5 text-center font-bold text-sm text-amber-300">{["🥇", "🥈", "🥉"][i]}</span>
                    <span className="text-lg">{g.cover_emoji}</span>
                    <span className="flex-1 min-w-0 text-sm font-semibold text-white truncate">{g.title}<span className="text-white/40 font-normal"> · {g.owner_name}</span></span>
                    <span className="text-xs text-white/60 shrink-0">{g.plays} plays</span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
